import { useTopicsProvider, useTopicsStoreProvider, getThreadStore, useThreadContext, useCommentContext, useScopedTopicsInstance } from "./Context"
import { generateThreadUrl, makeOptimisticComment } from "./utils"
import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import ToasterWrapper from '@/react/components/ToasterWrapper'
const { toast } = ToasterWrapper;


export function useFetchReplies(id) {
	const { API_CLIENT } = useTopicsProvider();
	const { id: threadId } = useThreadContext();

	return useQuery({
		queryKey: [`${threadId}/comments/${id}/replies`, threadId, id],
		queryFn: async () => {
			return (await API_CLIENT.get(`/${threadId}/comments/${id}/replies`)).data;
		},
		enabled: false, // don't auto-fetch on mount
	})
}

// mutation hooks that wrap Topics methods
export function usePostComment() {
	const topicsInstance = useTopicsProvider();
	return useMutation({
		mutationFn: async (payload) => {
			return await toast.promise(
				topicsInstance.postComment(payload),
				{
					loading: "Posting comment...",
					success: "Comment posted successfully!",
					error: "Failed to post comment.",
				}
			);
		},
		onError: (error) => {
			console.error('Failed to post comment:', error);
		}
	});
}

export function useUpdateComment({
	id,
	threadId,
}) {
	const topicsInstance = useTopicsProvider();

	return useMutation({
		mutationFn: async (payload) => {
			const updatedComment = {
				...makeOptimisticComment(payload),
				id,
				threadId,
			}

			// Store the promise
			const updatePromise = topicsInstance.updateComment(updatedComment);

			// Show toast (fire and forget)
			toast.promise(updatePromise, {
				loading: "Updating comment...",
				success: "Comment updated successfully!",
				error: "Failed to update comment.",
			});

			// Return the actual data
			return await updatePromise;
		},
		onSuccess: (data) => {
			topicsInstance.updateCommentInStore(id, data);
		},
		onError: (error) => {
			console.error('Failed to update comment:', error);
		}
	});
}

export function useDeleteComment({ id, threadId }) {
	const topicsInstance = useTopicsProvider();

	return useMutation({
		mutationFn: async () => {
			// Store the promise
			const deletePromise = topicsInstance.deleteComment({ id, threadId });

			// Show toast (fire and forget)
			toast.promise(deletePromise, {
				loading: "Deleting comment...",
				success: "Comment deleted successfully!",
				error: "Failed to delete comment.",
			});

			// Return the actual data
			return await deletePromise;
		},
		onError: (error) => {
			console.error('Failed to delete comment:', error);
		},
		onSuccess: (data) => {
			topicsInstance.deleteCommentFromStore(id);
		}
	});
}

export function useUpdateThread() {
	const topicsInstance = useTopicsProvider();

	return useMutation({
		mutationFn: async (payload) => {
			return await topicsInstance.updateThread(payload);
		},
		onError: (error) => {
			console.error('Failed to update thread:', error);
		}
	});
}

export function useDeleteThread() {
	const topicsInstance = useTopicsProvider();

	return useMutation({
		mutationFn: async (threadId) => {
			return await topicsInstance.deleteThread(threadId);
		},
		onError: (error) => {
			console.error('Failed to delete thread:', error);
		}
	});
}

export function useDeleteAllThreads() {
	const topicsInstance = useTopicsProvider();
	return useMutation({
		mutationFn: async (isResolved) => {
			return await toast.promise(
				topicsInstance.deleteAllThreads({isResolved}),
				{
					loading: `Deleting ${isResolved ? 'resolved' : 'unresolved'} Comments`,
					success: () => {
						topicsInstance.deleteAllThreadsFromStore(isResolved)
						return `${isResolved ? 'Resolved' : 'Unresolved'} Comments deleted successfully!`;
					},
					error: "Failed to delete Comments.",
				}
			);
		},
		onError: (error) => {
			console.error('Failed to delete all comments', error);
		}
	});
}

// ============================================
// THREAD ACTIONS HOOK (High-level wrapper)
// ============================================
export function useThreadActions(thread) {
	const topicsInstance = useScopedTopicsInstance();
	const { id } = thread;

	const updateThreadMutation = useUpdateThread();
	const deleteThreadMutation = useDeleteThread();

	const [isLoading, setIsLoading] = useState(false);

	const runAction = useCallback(async (mutationFn, messages) => {
		setIsLoading(true);
		try {
			await toast.promise(
				mutationFn().then((res) => {
					return res;
				}),
				{
					loading: messages.loading,
					success: messages.success,
					error: (err) => err.message || 'Action failed'
				}
			);
		} finally {
			setIsLoading(false);
		}
	}, []);

	const pin = useCallback(() =>
		runAction(
			async () => {
				const updatedThread = await updateThreadMutation.mutateAsync({
					threadId: id,
					isPinned: true
				});
				topicsInstance.updateThreadInStore(updatedThread);
				return updatedThread;
			},
			{
				loading: "Pinning thread...",
				success: "Thread pinned successfully"
			}
		), [id, updateThreadMutation, topicsInstance, runAction]
	);

	const unpin = useCallback(() =>
		runAction(
			async () => {
				const updatedThread = await updateThreadMutation.mutateAsync({
					threadId: id,
					isPinned: false
				});
				topicsInstance.updateThreadInStore(updatedThread);
				return updatedThread;
			},
			{
				loading: "Unpinning thread...",
				success: "Thread unpinned successfully"
			}
		), [id, updateThreadMutation, topicsInstance, runAction]
	);

	const resolve = useCallback(() =>
		runAction(
			async () => {
				const updatedThread = await updateThreadMutation.mutateAsync({
					threadId: id,
					isResolved: true
				});
				topicsInstance.updateThreadInStore(updatedThread);
				return updatedThread;
			},
			{
				loading: "Resolving thread...",
				success: "Thread resolved successfully"
			}
		), [id, updateThreadMutation, topicsInstance, runAction]
	);

	const unresolve = useCallback(() =>
		runAction(
			async () => {
				const updatedThread = await updateThreadMutation.mutateAsync({
					threadId: id,
					isResolved: false
				});
				topicsInstance.updateThreadInStore(updatedThread);
				return updatedThread;
			},
			{
				loading: "Unresolving thread...",
				success: "Thread unresolved successfully"
			}
		), [id, updateThreadMutation, topicsInstance, runAction]
	);

	const remove = useCallback(() =>
		runAction(
			async () => {
				await deleteThreadMutation.mutateAsync(id);
				topicsInstance.deleteThreadFromStore(id);
			},
			{
				loading: "Deleting thread...",
				success: "Thread deleted successfully"
			}
		), [id, deleteThreadMutation, topicsInstance, runAction]
	);

	const copy = useCallback(() => {
		const url = generateThreadUrl(thread);
		navigator.clipboard.writeText(url.toString());
		toast.success("Thread link copied to clipboard");
	}, [id]);

	return { pin, unpin, resolve, unresolve, remove, copy, isLoading };
}

export function useOptimisticComment({
	threadId,
	parentCommentId = null,
	onSuccessStoreFn    // sync: write real data to topics store
}) {
	const [optimisticList, setOptimisticList] = useState([]);
	const postComment = usePostComment();
	const { currentUser } = useTopicsProvider();

	const create = useCallback(async (payload) => {
		const optimistic = makeOptimisticComment({
			...payload,
			threadId,
			parentCommentId,
			user: currentUser
		});

		setOptimisticList(prev => [...prev, optimistic]);

		try {
			const real = await postComment.mutateAsync(optimistic);

			setOptimisticList(prev =>
				prev.filter(c => c.id !== optimistic.id)
			);

			onSuccessStoreFn(real);
		} catch (err) {
			setOptimisticList(prev =>
				prev.map(c =>
					c.id === optimistic.id
						? { ...c, isFailed: true, isPending: false }
						: c
				)
			);
		}
	}, [threadId, parentCommentId, currentUser, postComment, onSuccessStoreFn]);

	const retry = useCallback(async (optimistic) => {
		setOptimisticList(prev =>
			prev.map(c =>
				c.id === optimistic.id
					? { ...c, isPending: true, isFailed: false }
					: c
			)
		);

		try {
			const real = await postComment.mutateAsync(optimistic);

			setOptimisticList(prev =>
				prev.filter(c => c.id !== optimistic.id)
			);

			onSuccessStoreFn(real);
		} catch (err) {
			setOptimisticList(prev =>
				prev.map(c =>
					c.id === optimistic.id
						? { ...c, isFailed: true, isPending: false }
						: c
				)
			);
		}
	}, [postComment, onSuccessStoreFn]);

	return {
		optimisticList,
		create,
		retry
	};
}

export function useCreateThreadWithComment({
	onSuccess,
}) {
	const topicsInstance = useTopicsProvider();
	const { currentUser } = topicsInstance;

	const createThreadMutation = useMutation({
		mutationFn: async ({ thread, commentProps }) => {
			const comment = makeOptimisticComment({ ...commentProps, user: currentUser });
			const payload = {
				comment,
				...thread,
				user: currentUser,
			};
			const data = await topicsInstance.createThreadWithComment(payload);
			return data;
		},
		onError: (err) => {
			console.error(err);
		},
		onSuccess: (data) => {
			onSuccess && onSuccess(data);
			return data;
		}
	});
	const { mutateAsync, isPending } = createThreadMutation;

	return [
		useCallback(({ thread, commentProps }) => {
			return mutateAsync({ thread, commentProps });
		}, [topicsInstance]),
		isPending,
	]
}